import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../../services/authService';
import userManagementService from '../../services/userManagementService';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { hasActionAccess, hasFeatureAccess } from '../../utils/roleBasedAccess';

/**
 * User Management Page
 * For HR Managers to add, edit, and manage HR team members
 */
const UserManagementPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'recruiter',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get current user
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);

        // Check if user can manage users
        const canManage = await userManagementService.canManageUsers();
        if (!canManage) {
          throw new Error('You do not have permission to manage users');
        }

        // Load users
        await loadUsers();

      } catch (error) {
        console.error('[UserManagement] Error initializing:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const loadUsers = async (options = {}) => {
    try {
      const queryOptions = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchTerm,
        role: roleFilter,
        ...options
      };

      const result = await userManagementService.getHRUsers(queryOptions);
      setUsers(result.users || []);
      setPagination(prev => ({
        ...prev,
        total: result.total || 0,
        page: result.page || 1,
        pageSize: result.page_size || 20
      }));

    } catch (error) {
      console.error('[UserManagement] Error loading users:', error);
      setError(error.message);
    }
  };

  const handleAddUser = async () => {
    try {
      // Validate user data
      const validation = userManagementService.validateUserData(newUser);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return;
      }

      setValidationErrors({});
      
      // Create user
      await userManagementService.createHRUser(newUser);
      
      // Reset form and close modal
      setNewUser({ name: '', email: '', role: 'recruiter', password: '' });
      setShowAddUserModal(false);
      
      // Reload users
      await loadUsers();
      
    } catch (error) {
      console.error('[UserManagement] Error adding user:', error);
      setError(error.message);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await userManagementService.updateHRUser(userId, userData);
      await loadUsers();
      setEditingUser(null);
    } catch (error) {
      console.error('[UserManagement] Error updating user:', error);
      setError(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      await userManagementService.deleteHRUser(userId);
      await loadUsers();
    } catch (error) {
      console.error('[UserManagement] Error deleting user:', error);
      setError(error.message);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!confirm('Are you sure you want to reset this user\'s password?')) {
      return;
    }

    try {
      const result = await userManagementService.resetUserPassword(userId);
      alert(`Password reset successful. Temporary password: ${result.temporary_password}`);
    } catch (error) {
      console.error('[UserManagement] Error resetting password:', error);
      setError(error.message);
    }
  };

  const availableRoles = userManagementService.getAvailableRoles();

  // Apply search and filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers({ page: 1 });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, roleFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/hr-roles/manager" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading user management...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/hr-roles/manager" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => navigate('/hr-roles/manager')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <FixedSidebar currentPath="/hr-roles/manager" />
      
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'HR Roles', href: '/hr-roles' },
            { label: 'Manager Dashboard', href: '/hr-roles/manager' },
            { label: 'User Management', href: '/user-management', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">HR Team Management</h1>
              <p className="text-muted-foreground">
                Add and manage HR team members, coordinators, and recruiters
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => navigate('/hr-roles/manager')}
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Dashboard
              </Button>
              
              {/* Add Team Member - Only for Admin and HR Manager */}
              {hasActionAccess(currentUser?.role, 'userManagement', 'create-users') && (
                <Button onClick={() => setShowAddUserModal(true)}>
                  <Icon name="UserPlus" size={16} className="mr-2" />
                  Add Team Member
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Filter by Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Roles</option>
                  {availableRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={() => loadUsers()} className="w-full">
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Team Members ({pagination.total})
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((user) => {
                    const displayInfo = userManagementService.getUserDisplayInfo(user);
                    return (
                      <tr key={user.id} className="hover:bg-muted/25">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <img
                              src={displayInfo.avatar}
                              alt={user.name}
                              className="w-10 h-10 rounded-full mr-3"
                            />
                            <div>
                              <div className="text-sm font-medium text-foreground">{user.name}</div>
                              <div className="text-sm text-muted-foreground">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                            {displayInfo.roleLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            user.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {displayInfo.statusLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {displayInfo.createdAtFormatted}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          {/* Edit User - Available to Admin and HR Manager */}
                          {hasActionAccess(currentUser?.role, 'userManagement', 'edit-users') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingUser(user)}
                            >
                              <Icon name="Edit" size={14} />
                            </Button>
                          )}
                          
                          {/* Reset Password - Available to Admin and HR Manager */}
                          {hasActionAccess(currentUser?.role, 'userManagement', 'edit-users') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPassword(user.id)}
                            >
                              <Icon name="Key" size={14} />
                            </Button>
                          )}
                          
                          {/* Delete User - Only for Admin */}
                          {hasActionAccess(currentUser?.role, 'userManagement', 'delete-users') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Icon name="Trash2" size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No team members found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || roleFilter ? 'Try adjusting your filters' : 'Add your first team member to get started'}
                </p>
                {!searchTerm && !roleFilter && (
                  <Button onClick={() => setShowAddUserModal(true)}>
                    <Icon name="UserPlus" size={16} className="mr-2" />
                    Add Team Member
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Add User Modal */}
          {showAddUserModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-foreground">Add Team Member</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddUserModal(false);
                      setValidationErrors({});
                      setNewUser({ name: '', email: '', role: 'recruiter', password: '' });
                    }}
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Name *</label>
                    <input
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        validationErrors.name ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter full name"
                    />
                    {validationErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        validationErrors.email ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter email address"
                    />
                    {validationErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Role *</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {availableRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Password *</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        validationErrors.password ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter initial password"
                    />
                    {validationErrors.password && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      User can change this password after first login
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddUserModal(false);
                      setValidationErrors({});
                      setNewUser({ name: '', email: '', role: 'recruiter', password: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddUser}>
                    <Icon name="UserPlus" size={16} className="mr-2" />
                    Add Member
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserManagementPage;